<template>
    <div>
        <div @click="triggerExportDialog(exportTo.XLSX)" class="menu-action_item">
            <div class="flex items-center">
                <i class="vc-icon-download icon-md text-primary"/>
                <p class="text-md mx-1">{{ $t('general.export.excel') }}</p>
            </div>
        </div>
        <div @click="triggerExportDialog(exportTo.CSV)" class="menu-action_item">
            <div class="flex items-center">
                <i class="vc-icon-download icon-md text-primary"/>
                <p class="text-md mx-1">{{ $t('general.export.csv') }}</p>
            </div>
        </div>
        <modal :visible.sync="showExportDialog" v-if="showExportDialog" :append-to-body="true">
            <div class="flex flex-row items-center pb-4">
                <h3 class="text-main-2xl font-semibold main-text-color" slot="title">{{ $t('general.exportData') }}</h3>
                <static-widget-info :widget="widget" class="px-2"/>
            </div>
            <div class="pb-4">
                <label class="main-text-color">{{ $t('widget.export.fileName') }}</label>
                <el-input v-model="fileName"/>
            </div>
            <el-alert
                :title="$t('widget.export.exportDataTo') +' '+format"
                type="info"
                :description="$t('widget.export.exportFileColumnOrder')"
                :closable="false"
                show-icon
            />
            <div class="py-6 border-b flex">
                <el-radio v-model="exportFormat" :label="exportTo.CSV">{{ $t('general.export.csv') }}</el-radio>
                <el-radio v-model="exportFormat" :label="exportTo.XLSX">{{ $t('general.export.excel') }}</el-radio>
            </div>
            <slot :onManageExport="onManageExport"/>
            <template v-slot:footer>
                <div class="border-t-2 border-gray-300 py-4 px-10 flex items-center justify-between main-bg-light">
                    <cancel-button
                        @on-click="showExportDialog = false"
                    />
                    <el-button @click="exportTableData"
                               :disabled="loading || !columnsToExport.length"
                               :loading="loading"
                               type="primary">{{ $t('widget.export.export') }}
                    </el-button>
                </div>
            </template>
        </modal>
    </div>
</template>
<script>
    import XLSX from 'xlsx'
    import { format } from 'date-fns'
    import { Alert, Radio } from 'element-ui'
    import DownloadIcon from 'vue-feather-icons/icons/DownloadIcon'
    import { isARealtimeTableWidget, timeFilterToHuman } from '@/helpers/widgetUtils'
    import { DATE_TIME_COLUMNS, DATE_FORMAT, DATE_TIME_FORMAT } from '@/helpers/table'
    import tableColumnsMixin from "@/mixins/tableColumnsMixin";
    import {getTranslationOrUndefined} from "@/helpers/translation";
    import startCase from "lodash/startCase";
    import get from "lodash/get";

    export default {
        inheritAttrs: false,
        name: 'export-data',
        mixins: [tableColumnsMixin],
        components: {
            [Alert.name]: Alert,
            [Radio.name]: Radio,
            StaticWidgetInfo: () => import('@/components/Widgets/WidgetUpdateForm/StaticWidgetInfo'),
            ManageColumns: () => import('./ManageColumns'),
            DownloadIcon,
            Modal: () => import('@/components/Common/Modal'),
            CancelButton: () => import("@/components/Common/Buttons/CancelButton")
        },
        props: {
            tableId: {
                type: String,
                default: '',
            },
            widgetTitle: {
                type: String,
                default: '- -',
            },
            allRecords: {
                type: Array,
                default: () => [],
            },
            widget: {
                type: Object,
                default: () => ({}),
            },
            columnsToExport: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                onManageExport: true,
                exportTo: {
                    XLSX: '.xlsx',
                    CSV: '.csv',
                },
                exportToDictionary: {
                    '.xlsx': 'Excel',
                    '.csv': 'CSV',
                },
                exportFormat: '',
                showExportDialog: false,
                loading: false,
                fileName: '--',
            }
        },
        computed: {
            format() {
                return this.$t(this.exportToDictionary[this.exportFormat])
            },
        },
        methods: {
            triggerExportDialog(exportFormat) {
                this.showExportDialog = true
                this.exportFormat = exportFormat
                this.fileName = this.$t(this.widgetTitle)
            },
            getFileName() {
                let fileName = this.fileName || this.$t('widget.title')
                let timeRange = timeFilterToHuman(this.widget, 'dd-MM-yyyy')

                return fileName + ' ' + timeRange + this.exportFormat
            },
            exportTableData() {
                this.loading = true
                let fileName = this.getFileName()

                if (isARealtimeTableWidget(this.widget)) {
                    this.$emit('on-update-layout', true)
                    this.showExportDialog = false

                    const tableId = this.tableId

                    let tableElement = document.getElementById(tableId)
                    let excelWorkBook = XLSX.utils.table_to_book(tableElement)

                    XLSX.writeFile(excelWorkBook, fileName)

                    this.closeModal()

                    return
                }

                // As sorting of columns goes with the order of the objects in the Column.availableColumns array but columnsToExport is an array of prop names which are not affected by the order, need to sort the columnsToExport array based on the order of the availableColumns array
                const columnsToExportSet = new Set(this.columnsToExport)
                const columnsToExport = []

                for (const obj of get(this.widget, 'WidgetLayout.Columns.availableColumns', [])) {
                    if (columnsToExportSet.has(obj.prop)) {
                        columnsToExport.push(obj.prop)
                    }
                }

                let records = []

                this.allRecords.forEach((record) => {
                    let data = {}
                    columnsToExport.forEach(key => {
                        let templateColumnTranslation = undefined

                        if (this.widget.WidgetTemplateColumns.length) {
                            const keyColumn = this.widget.WidgetTemplateColumns.find(column => column.WidgetTemplateColumnsName === key)

                            if (keyColumn) {
                                templateColumnTranslation = getTranslationOrUndefined(keyColumn.TemplateNameContentTag)
                                    || getTranslationOrUndefined(keyColumn.WidgetTemplateColumnsName)
                                    || startCase(keyColumn.WidgetTemplateColumnsName)
                            }
                        }

                        const translation = this.makeTableHeaderTranslationsByProp(templateColumnTranslation) || this.makeTableHeaderTranslationsByProp(key)
                        data[translation] = this.tryFormatCellTypeDate(record, key)
                    })
                    records.push(data)
                })

                let data = XLSX.utils.json_to_sheet(records)
                let excelWorkBook = XLSX.utils.book_new()

                XLSX.utils.book_append_sheet(excelWorkBook, data, 'data')
                XLSX.writeFile(excelWorkBook, fileName)

                this.$emit('on-update-layout', true)
                this.closeModal()
            },
            tryFormatCellTypeDate(record, key) {
                if (!record[key]) {
                    return ''
                }
                if (DATE_FORMAT.includes(key.toLowerCase())) {
                    try {
                        // To prevent date-fns errors like: Invalid time value
                        record[key] = record[key].replace('Z', '')
                        return format(new Date(record[key]), DATE_FORMAT)
                    } catch (e) {
                        return record[key].replace(/\//g, '-')
                    }
                }
                if (DATE_TIME_COLUMNS.includes(key.toLowerCase())) {
                    try {
                        // To prevent date-fns errors like: Invalid time value
                        record[key] = record[key].replace('Z', '')
                        return format(new Date(record[key]), DATE_TIME_FORMAT)
                    } catch (e) {
                        return record[key].replace(/\//g, '-')
                    }
                }
                return record[key]
            },
            closeModal () {
                this.loading = false
                this.showExportDialog = false
            }
        },
    }
</script>

<style lang="scss" scoped>
.menu-action_item {
    @apply w-full flex items-center p-2 cursor-pointer;
    color: var(--mainTextColor-color);
    &:hover {
        color: var(--primary-color);
    }
}
::v-deep .el-radio__inner {
    background-color: var(--mainBgColor-color);
}
::v-deep .el-radio__label {
    color: var(--mainTextColor-color);
}
::v-deep .el-alert--info.is-light {
    background-color: var(--mainBgColor-color);
    color: var(--mainTextColor-color);
}
::v-deep .el-alert__description {
    color: var(--mainTextColor-color);
}
</style>
